/**
 * ★★ WO — A CONSULTA DO PAINEL: a timeline de um DIA, numa consulta.
 *
 * ⚠⚠ ESTE ARQUIVO EXISTE NESTA FORMA POR CAUSA DE UM DEFEITO MEDIDO.
 *
 * A revisão 1 do plano ancorava o painel em `leads.atualizadoEm`: buscar os leads tocados no
 * dia e ler as interações deles. Medido em produção: das 54 tentativas de setembro, **24
 * (44%)** tinham `atualizadoEm` em **dia diferente** da tentativa — o lead foi tocado de novo
 * depois. ★★ Um painel de dia passado erraria 44% dos dias, **parecendo certo**.
 *
 * ★ A saída foi o `clinicaId` na interação (lote 1) + a regra de collectionGroup + o índice
 * `(clinicaId, criadoEm)`. Agora é UMA consulta, e o histórico inteiro é legível.
 *
 * ★★ E o FUSO mora na faixa, não num campo. A revisão 2 propunha gravar `dia` na interação;
 * ele vinha do relógio do CLIENTE, era forjável e contradizia a regra de ouro da peça. Saiu.
 * `criadoEm` é `serverTimestamp()`, e quem converte o dia da clínica em faixa UTC é o
 * `faixaDoDiaNaClinica` do `dashboard/periodo.js` — ★ um lugar só, já testado.
 *
 * ⚠ NADA de `atualizadoEm` aqui. Há guarda de fonte cobrando isso.
 */

import { collection, collectionGroup, getDocs, getDocsFromServer, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { faixaDoDiaNaClinica, faixaDoMesNaClinica } from '@/dashboard/periodo';
import { tratarErro } from '@/lib/erros';
/**
 * ★★★ WT lote 1 — AS CONSULTAS DESTE SERVIÇO REGISTRAM O ERRO.
 *
 * ⚠ Elas são `getDocs` diretos (não `onSnapshot`), então não passavam por `tratarErro`. ★ E
 * são justamente as dos Indicadores e do Placar: quando falham, a grade sai **sem cor** e o
 * placar **não aparece** — a tela avisa (`setErro90`, por exemplo), mas o superadmin não
 * ficava sabendo.
 *
 * ★ Registra e **relança**: quem chama já trata o erro, e engolir aqui apagaria o aviso.
 */
const comRegistro = (nome, fn) => async (...args) => {
  try {
    return await fn(...args);
  } catch (erro) {
    tratarErro(nome, null, 'service')(erro);
    throw erro;
  }
};

/**
 * ★★ Indicadores (01/11/2026) — `soServidor`: a resposta tem de vir do SERVIDOR.
 *
 * ⚠ Medido no emulador com a rede cortada: o `getDocs` devolveu o que havia na cópia local
 * (nada) SEM erro, e a tela mostrou a grade zerada e um resumo para copiar com
 * "Ligações: 0". ★ Com `soServidor`, sem servidor a consulta FALHA (`unavailable`) e quem
 * chama mostra o erro. ⚠ Só os Indicadores pedem; as outras telas seguem como estavam.
 */
const ler = (q, { soServidor = false } = {}) => (soServidor ? getDocsFromServer(q) : getDocs(q));

/**
 * As interações de UM dia da clínica.
 *
 * @param clinicaId  o tenant — a regra de collectionGroup o exige
 * @param referencia qualquer instante dentro do dia desejado
 */
async function buscarInteracoesDoDiaCru(clinicaId, referencia = new Date(), opcoes = {}) {
  if (!clinicaId) throw new Error('buscarInteracoesDoDia: clinicaId é obrigatório.');
  const { de, ate } = faixaDoDiaNaClinica(referencia);
  const q = query(
    collectionGroup(db, 'interacoes'),
    where('clinicaId', '==', clinicaId),
    where('criadoEm', '>=', de),
    where('criadoEm', '<=', ate),
    orderBy('criadoEm', 'asc'),
  );
  const snap = await ler(q, opcoes);
  return snap.docs.map((d) => ({
    id: d.id,
    // ★ O `leadId` NÃO é campo da interação: ele vem do CAMINHO
    // (`clinicas/{c}/leads/{leadId}/interacoes/{id}`). Derivar daqui evita um campo novo.
    leadId: d.ref.parent.parent?.id ?? null,
    ...d.data(),
  }));
}

/**
 * As interações de uma JANELA de dias (para as taxas acumuladas).
 *
 * ⚠ Mesma consulta, só com a faixa maior. ★ O índice `(clinicaId, criadoEm)` serve às duas.
 */
async function buscarInteracoesEntreCru(clinicaId, de, ate, opcoes = {}) {
  if (!clinicaId) throw new Error('buscarInteracoesEntre: clinicaId é obrigatório.');
  const q = query(
    collectionGroup(db, 'interacoes'),
    where('clinicaId', '==', clinicaId),
    where('criadoEm', '>=', de),
    where('criadoEm', '<=', ate),
    orderBy('criadoEm', 'asc'),
  );
  const snap = await ler(q, opcoes);
  return snap.docs.map((d) => ({
    id: d.id,
    leadId: d.ref.parent.parent?.id ?? null,
    ...d.data(),
  }));
}

/**
 * ★★ OS AGENDAMENTOS QUE UM DIA PRECISA — e são DOIS recortes, não um.
 *
 * ⚠ Por que não `ouvirAgendamentos` com uma janela de `dataHora`: o painel conta `agendou`
 * pelo **`criadoEm`** do agendamento, e `dataHora` pode estar meses à frente. Uma janela de
 * `dataHora` ao redor do dia **perderia silenciosamente** os agendamentos marcados no dia
 * para datas distantes — exatamente o tipo de erro que parece certo na tela.
 *
 * ★ Então são dois recortes, e cada um serve a uma conta:
 *
 *   1. `criadoEm` no dia  → `agendou` e `agendouSemBaixa` (quem MARCOU hoje);
 *   2. `dataHora` no dia  → a RESERVA do comparecimento, para os leads anteriores à VE que
 *      não têm `compareceuEm`: `dataComparecimento` cai no agendamento `compareceu` mais
 *      antigo, e ele tem `dataHora` no dia.
 *
 * ★★ E por que isto NÃO perde comparecimento: `dataComparecimento` olha **`lead.compareceuEm`
 * primeiro** — campo do LEAD, que o `LeadsProvider` já tem inteiro em memória. O agendamento
 * só entra na reserva. Há um teste que prova a igualdade contra a lista completa.
 *
 * ★ Nenhum índice composto: as duas são FAIXA DE UM CAMPO só, em escopo de coleção — onde o
 * índice de campo único é automático. (⚠ Não confunda com o `collectionGroup` acima, que
 * precisou de índice declarado justamente porque automático só vale em COLLECTION.)
 */
async function buscarAgendamentosDoDiaCru(clinicaId, referencia = new Date(), opcoes = {}) {
  if (!clinicaId) throw new Error('buscarAgendamentosDoDia: clinicaId é obrigatório.');
  const { de, ate } = faixaDoDiaNaClinica(referencia);
  const col = collection(db, 'clinicas', clinicaId, 'agendamentos');
  const faixa = (campo) => query(col, where(campo, '>=', de), where(campo, '<=', ate));

  const [marcados, naAgenda] = await Promise.all([
    ler(faixa('criadoEm'), opcoes),
    ler(faixa('dataHora'), opcoes),
  ]);

  // ★ União deduplicada: um agendamento marcado para o próprio dia cai nas duas consultas, e
  // contá-lo duas vezes inflaria `agendou`.
  const porId = new Map();
  for (const snap of [marcados, naAgenda]) {
    for (const d of snap.docs) porId.set(d.id, { id: d.id, ...d.data() });
  }
  return [...porId.values()];
}

/**
 * ★★ WO lote 2b — O MÊS INTEIRO, numa consulta só.
 *
 * ⚠⚠ NÃO são 30 chamadas de `buscarInteracoesDoDia`. A grade precisa do mês, e trinta
 * idas ao servidor para montar uma tela seria o custo de desenho pago em latência.
 *
 * ★★ E NÃO precisa de índice novo — isto foi conferido contra produção, não suposto. O
 * índice `(clinicaId, criadoEm)` que o lote 1 criou serve ao dia, ao mês e à janela de 90
 * dias, porque **as três levam faixa de `criadoEm`**.
 *
 * ⚠ Medido em 3/out: pedir `collectionGroup('interacoes').where('clinicaId','==',…)` SEM a
 * faixa devolve `9 FAILED_PRECONDITION: requires a COLLECTION_GROUP_ASC index`. Índice de
 * campo único é automático em COLLECTION, **não** em COLLECTION_GROUP. ★ Toda consulta nova
 * de interação leva a faixa junto — não é estilo, é o que o servidor aceita.
 *
 * ★ Volume medido: setembro inteiro = **1.993** interações. Uma consulta.
 */
async function buscarInteracoesDoMesCru(clinicaId, mes, opcoes = {}) {
  if (!clinicaId) throw new Error('buscarInteracoesDoMes: clinicaId é obrigatório.');
  const { de, ate } = faixaDoMesNaClinica(mes);
  return buscarInteracoesEntre(clinicaId, de, ate, opcoes);
}

/**
 * Os agendamentos de um MÊS — ★ pelos MESMOS dois recortes do dia, e pelo mesmo motivo.
 *
 * ⚠ `agendou` conta pelo `criadoEm`; a reserva do comparecimento (leads anteriores à VE, sem
 * `compareceuEm`) precisa do `dataHora`. Uma faixa só perderia silenciosamente um dos dois.
 * ★ A união é deduplicada: o agendamento marcado e atendido no mesmo mês cai nas duas.
 */
async function buscarAgendamentosDoMesCru(clinicaId, mes, opcoes = {}) {
  if (!clinicaId) throw new Error('buscarAgendamentosDoMes: clinicaId é obrigatório.');
  const { de, ate } = faixaDoMesNaClinica(mes);
  const col = collection(db, 'clinicas', clinicaId, 'agendamentos');
  const faixa = (campo) => query(col, where(campo, '>=', de), where(campo, '<=', ate));

  const [marcados, naAgenda] = await Promise.all([
    ler(faixa('criadoEm'), opcoes),
    ler(faixa('dataHora'), opcoes),
  ]);
  const porId = new Map();
  for (const snap of [marcados, naAgenda]) {
    for (const d of snap.docs) porId.set(d.id, { id: d.id, ...d.data() });
  }
  return [...porId.values()];
}

/**
 * ★ WO lote 3b — os agendamentos de uma JANELA, pelos mesmos dois recortes.
 *
 * ⚠ `criadoEm` para "quem marcou nesta semana"; `dataHora` para a reserva do comparecimento
 * e para as pendências (vencido sem baixa). União deduplicada, como no dia e no mês.
 */
async function buscarAgendamentosEntreCru(clinicaId, de, ate) {
  if (!clinicaId) throw new Error('buscarAgendamentosEntre: clinicaId é obrigatório.');
  const col = collection(db, 'clinicas', clinicaId, 'agendamentos');
  const faixa = (campo) => query(col, where(campo, '>=', de), where(campo, '<=', ate));
  const [marcados, naAgenda] = await Promise.all([
    getDocs(faixa('criadoEm')),
    getDocs(faixa('dataHora')),
  ]);
  const porId = new Map();
  for (const snap of [marcados, naAgenda]) {
    for (const d of snap.docs) porId.set(d.id, { id: d.id, ...d.data() });
  }
  return [...porId.values()];
}

/**
 * ★★ Indicadores (01/11/2026) — TODOS os agendamentos de alguns leads, para o resumo de hoje.
 *
 * ⚠ Por que existe: o resumo passou a ler só o DIA, e o comparecimento é creditado ao
 * `compareceu` MAIS ANTIGO do lead (`creditoComparecimento`) — que pode ser de outro dia do
 * mês. Sem estes, o crédito do resumo poderia divergir da grade. ★ Quem chama passa só os
 * leads com comparecimento possível hoje (poucos) e recorta pelo mês.
 *
 * ★ `in` sobre UM campo (`leadId`), em escopo de coleção: índice de campo único, automático —
 * nenhum índice novo. Lotes de 30, o limite do `in`.
 */
async function buscarAgendamentosDosLeadsCru(clinicaId, leadIds = [], opcoes = {}) {
  if (!clinicaId) throw new Error('buscarAgendamentosDosLeads: clinicaId é obrigatório.');
  const ids = [...new Set((leadIds ?? []).filter(Boolean))];
  const col = collection(db, 'clinicas', clinicaId, 'agendamentos');
  const lotes = [];
  for (let i = 0; i < ids.length; i += 30) lotes.push(ids.slice(i, i + 30));
  const snaps = await Promise.all(lotes.map((lote) => ler(query(col, where('leadId', 'in', lote)), opcoes)));
  const porId = new Map();
  for (const snap of snaps) {
    for (const d of snap.docs) porId.set(d.id, { id: d.id, ...d.data() });
  }
  return [...porId.values()];
}

/**
 * ★★ PC-2 — as consultas FUTURAS da clínica (`dataHora ≥ agora`), para o cartão "Leads sem
 * próximo contato" não cobrar lead agendado que tem consulta marcada (decisão 1 do dono).
 *
 * ⚠ A janela de 8 dias do placar só traz consulta futura MARCADA nos últimos 8 dias; uma
 * marcada há 10 dias para a semana que vem escaparia. ★ Campo único (`dataHora`), sem índice
 * novo — medido em produção: 64 documentos em Campinas, sem `FAILED_PRECONDITION`.
 */
async function buscarAgendamentosFuturosCru(clinicaId, agora) {
  if (!clinicaId) throw new Error('buscarAgendamentosFuturos: clinicaId é obrigatório.');
  const col = collection(db, 'clinicas', clinicaId, 'agendamentos');
  const snap = await getDocs(query(col, where('dataHora', '>=', agora)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export const buscarInteracoesDoDia = comRegistro('as interações do dia', buscarInteracoesDoDiaCru);
export const buscarInteracoesEntre = comRegistro('o acumulado do período', buscarInteracoesEntreCru);
export const buscarAgendamentosDoDia = comRegistro('os agendamentos do dia', buscarAgendamentosDoDiaCru);
export const buscarInteracoesDoMes = comRegistro('as interações do mês', buscarInteracoesDoMesCru);
export const buscarAgendamentosDoMes = comRegistro('os agendamentos do mês', buscarAgendamentosDoMesCru);
export const buscarAgendamentosEntre = comRegistro('os agendamentos do período', buscarAgendamentosEntreCru);
export const buscarAgendamentosFuturos = comRegistro('as consultas futuras', buscarAgendamentosFuturosCru);
export const buscarAgendamentosDosLeads = comRegistro('os agendamentos dos leads', buscarAgendamentosDosLeadsCru);
